import { MemoExoticComponent, useEffect, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import Loading from "_shared/components/Loading/Loading";
import { useUserSubscriptionsQuery } from "_shared/graphql";
import { stringToUrl } from "_shared/helpers/formatting";
import { Plan } from "_shared/models/Plan";
import CFTCFuturesPositioning from "./cftc-futures-positioning/CFTCFuturesPositioning";
import CrossAssetClassVolatility from "./cross-asset-class-volatility/CrossAssetClassVolatility";
import DoomsdayDozen from "./doomsday-dozen/DoomsdayDozen";
import EtfFundsFlows from "./etf-funds-flows/EtfFundsFlows";
import IVolLandscape from "./ivol-landscape/IVolLandscape";
import MarketInsights from "./market-insights/MarketInsights";
import NotFound from "./not-found/NotFound";
import DashboardTable from "./tickers/DashboardTable";
import Top25 from "./top-25/Top25";
import UpsellModal from "./_root/components/UpsellModal/UpsellModal";
import { useUserPanels } from "./_root/contexts/UserPanelsContext";
import VIPView from "./tickers/VIPView";
import Chat from "./tickers/table/Chat";

type PanelRouteOptions = {
  Component?:
  | ((props: any) => JSX.Element)
  | MemoExoticComponent<(props: any) => JSX.Element>;
  plans?: Plan[];
  addon?: string;
};

// panel route components and plan restrictions
// unlisted defaults to DashboardTable and no restrictions
const routeMap: Record<string, PanelRouteOptions> = {
  "futures-contract-positioning": {
    Component: CFTCFuturesPositioning,
    plans: ["plus_plan", "core_plan", "pro_plan", "master_plan"],
  },
  "asset-class-volatility": {
    Component: CrossAssetClassVolatility,
    plans: ["basic_plan", "plus_plan", "core_plan", "pro_plan", "master_plan"],
  },
  "market-insights": {
    Component: MarketInsights,
  },
  "ivol-landscape": {
    Component: IVolLandscape,
    plans: ["plus_plan", "core_plan", "pro_plan", "master_plan"],
  },
  "top-25": {
    Component: Top25,
    addon: 'top25_dashboard',
  },
  "doomsday-dozen": {
    Component: DoomsdayDozen,
    plans: ["free_plan", "plus_plan", "core_plan", "core_plan", "pro_plan", "master_plan"],
  },
  "etf-heat-map-&-fund-flows": {
    Component: EtfFundsFlows,
  },
  "index-futures": {
    plans: ["core_plan", "pro_plan", "master_plan"],
  },
  "vip-view": {
    Component: VIPView,
    plans: ["master_plan"],
  },
  "bullseye-ai-trade-ideas": {
    Component: VIPView,
    addon: "bullseye_ai_trade_ideas",
  },
  "chat": {
    Component: Chat,
  },
  "global-equity-etfs": {
    addon: "equity_dashboard",
  },
  "us-sector-etfs": {
    addon: "sector_dashboard",
  },
  "global-commodity-etfs": {
    addon: "commodity_dashboard",
  },
  "global-fixed-income-etfs": {
    addon: "income_dashboard",
  },
  "crypto": {
    addon: "crypto_dashboard",
  },
  "global-currency-etfs": {
    addon: "currency_dashboard",
  },
  "42-macro": {
    addon: "macro_dashboard",
  },
};

const DashboardRoutes: React.FC = () => {
  const location = useLocation();
  const path2 = location.pathname.split("/")[3];

  const { data: subscriptionsData } = useUserSubscriptionsQuery();

  // derived from user subscriptions
  const currentPlan = ([...(subscriptionsData?.subscriptions ?? [])]
    ?.sort((a, b) => b.monthly_daily_price - a.monthly_daily_price)
    ?.find((sub) => sub.subscribed && sub.id.endsWith("plan"))?.id ??
    null) as Plan | null;
  const currentAddons = ([...(subscriptionsData?.subscriptions ?? [])]
    ?.filter(sub => sub.subscribed && !sub.id.endsWith('plan'))
    ?.map(sub => sub.id));
  const planInterval =
    subscriptionsData?.subscriptions?.find(
      (sub) => sub.subscribed && sub.id.endsWith("plan"),
    )?.subscribed?.frequency ?? "";
  const alertsInterval =
    subscriptionsData?.subscriptions?.find(
      (sub) => sub.subscribed && sub.id === "signal_alerts",
    )?.subscribed?.frequency ?? "";

  const { panels, fetchPanels, loading } = useUserPanels();

  if (loading) return <Loading />;
  return (
    <Switch>
      {/* dashboard defaults to first panel */}
      <Route exact path="/dashboard">
        <Redirect
          to={`/dashboard/${stringToUrl(panels[0]?.name ?? "")}${panels[0]?.name ? `/${localStorage.getItem("view")}-view` : ""
            }`}
        />
      </Route>
      {/* create a route for each available panel */}
      {panels.map((panel) => {
        const panelUrl = stringToUrl(panel.name);
        const routeOptions = routeMap[panelUrl];
        return (
          <Route path={`/dashboard/${panelUrl}`} key={panel.id}>
            {routeOptions?.addon ?
              (currentAddons?.includes(routeOptions?.addon) ? (
                routeOptions?.Component ? (
                  <routeOptions.Component
                    fetchPanels={fetchPanels}
                    bullseye={routeOptions?.addon === 'bullseye_ai_trade_ideas' ? true : null}
                    reload={false}
                    tab={panel}
                    planInterval="daily"
                  />
                ) : (
                  <DashboardTable
                    fetchPanels={fetchPanels}
                    data={panel}
                    kiss={path2 === "kiss-view" ? true : false}
                    reload={loading}
                    planInterval={planInterval}
                    alertsInterval={alertsInterval}
                  />
                )) : <UpsellModal upgradeToAddon={routeOptions?.addon} />)
              :
              (!routeOptions?.plans ||
                (currentPlan && routeOptions.plans.includes(currentPlan)) ? (
                routeOptions?.Component ? (
                  <routeOptions.Component
                    fetchPanels={fetchPanels}
                    reload={false}
                    tab={panel}
                    planInterval="daily"
                    bullseye={(panelUrl === 'vip-view') ? true : null}
                  />
                ) : (
                  <DashboardTable
                    fetchPanels={fetchPanels}
                    data={panel}
                    kiss={path2 === "kiss-view" ? true : false}
                    reload={loading}
                    planInterval={planInterval}
                    alertsInterval={alertsInterval}
                  />
                )
              ) : (
                <UpsellModal upgradeToPanel={panel?.name} />
              ))}
          </Route>
        );
      })}
      {/* fallback */}
      <Route path="/dashboard/*">
        <NotFound />
      </Route>
    </Switch>
  );
};

export default DashboardRoutes;
